import firebase from '../../../../../../firebase';
import { openSnackbar } from '../../../../../../app/modules/snackbar/actions';
import { WORKFORCE_DELETED } from '../../config';
import { objectToArray, arrayToObject } from '../../../../../../utils';

export const REQUEST_WORKFORCE_MEMBERS = 'REQUEST_WORKFORCE_MEMBERS';
export const RECEIVE_WORKFORCE_MEMBERS = 'RECEIVE_WORKFORCE_MEMBERS';
export const RECEIVE_MORE_WORKFORCE_MEMBERS = 'RECEIVE_MORE_WORKFORCE_MEMBERS';
export const RECEIVE_WORKFORCE_GROUP_DATA = 'RECEIVE_WORKFORCE_GROUP_DATA';
export const REPORT_WORKFORCE_MEMBERS_REQUEST_ERROR =
  'REPORT_WORKFORCE_MEMBERS_REQUEST_ERROR';
export const SELECT_WORKFORCE_MEMBER = 'SELECT_WORKFORCE_MEMBER';
export const FILTER_WORKFORCE_MEMBER_DATA = 'FILTER_WORKFORCE_MEMBER_DATA';
export const SET_WORKFORCE_MEMBER_FILTER_QUERY = 'SET_WORKFORCE_MEMBER_FILTER_QUERY';
export const SELECT_ALL_WORKFORCE_MEMBERS = 'SELECT_ALL_WORKFORCE_MEMBERS';
export const DESELECT_ALL_WORKFORCE_MEMBERS = 'DESELECT_ALL_WORKFORCE_MEMBERS';
export const WORKFORCE_DELETE_ERROR = 'WORKFORCE_DELETE_ERROR';
export const WORKFORCE_MEMBER_DELETED = 'WORKFORCE_MEMBER_DELETED';
export const REQUEST_MORE_WORKFORCE_MEMBERS = 'REQUEST_MORE_WORKFORCE_MEMBERS';
export const SET_WORKFORCE_TOTAL_COUNT = 'SET_WORKFORCE_TOTAL_COUNT';

const PAGE_SIZE = 100;
const PREFETCH_DELAY_FIRST_MS = 1500;
const PREFETCH_DELAY_NEXT_MS = 800;

const requestTypes = () => ({
  type: REQUEST_WORKFORCE_MEMBERS,
});

export const selectType = key => ({
  type: SELECT_WORKFORCE_MEMBER,
  key,
});

const processSnapshot = snap => {
  if (!snap.exists()) return { workforceMembers: {}, lastKey: null, count: 0 };
  const val = snap.val();
  const users = objectToArray(val);
  const workforceMembers = arrayToObject(
    users.filter(i => i.value && i.value.workforceMember),
  );
  const rawKeys = Object.keys(val).sort();
  const lastKey = rawKeys.length > 0 ? rawKeys[rawKeys.length - 1] : null;
  return { workforceMembers, lastKey, count: rawKeys.length };
};

export const getWorkforceList = app => dispatch => {
  dispatch(requestTypes());

  const ref = firebase.database().ref(`app_users/${app}`);
  ref
    .orderByKey()
    .limitToFirst(PAGE_SIZE)
    .once('value')
    .then(snap => {
      const { workforceMembers, lastKey, count } = processSnapshot(snap);
      const hasMore = count === PAGE_SIZE;
      dispatch({
        type: RECEIVE_WORKFORCE_MEMBERS,
        value: workforceMembers,
        lastKey,
        hasMore,
      });
      dispatch(getWorkforceTotalCount(app));
      if (hasMore) {
        setTimeout(() => dispatch(loadMoreWorkforceMembers(app)), PREFETCH_DELAY_FIRST_MS);
      }
    })
    .catch(err => {
      dispatch({
        type: REPORT_WORKFORCE_MEMBERS_REQUEST_ERROR,
        err: err && err.message ? err.message : 'Failed to load',
      });
    });
};

export const getWorkforceTotalCount = app => dispatch => {
  const ref = firebase.database().ref(`app_users/${app}`);
  ref
    .once('value')
    .then(snap => {
      const { workforceMembers } = processSnapshot(snap);
      const count = Object.keys(workforceMembers).length;
      dispatch({ type: SET_WORKFORCE_TOTAL_COUNT, count });
    })
    .catch(() => { });
};

export const fetchAllWorkforceMembersForExport = app => dispatch =>
  firebase
    .database()
    .ref(`app_users/${app}`)
    .once('value')
    .then(snap => {
      const { workforceMembers } = processSnapshot(snap);
      return workforceMembers;
    });

export const loadMoreWorkforceMembers = app => (dispatch, getState) => {
  const { lastKey, hasMore, loadingMore } =
    getState().auth.workforceMembers.listPage.data;
  if (!hasMore || !lastKey || loadingMore) return Promise.resolve();

  dispatch({ type: REQUEST_MORE_WORKFORCE_MEMBERS });

  const ref = firebase.database().ref(`app_users/${app}`);
  return ref
    .orderByKey()
    .startAt(lastKey)
    .limitToFirst(PAGE_SIZE + 1)
    .once('value')
    .then(snap => {
      const {
        workforceMembers: rawMembers,
        lastKey: newLastKey,
        count,
      } = processSnapshot(snap);
      const keys = Object.keys(rawMembers).sort();
      const skipFirst = keys[0] === lastKey ? 1 : 0;
      const newMembers = {};
      keys.slice(skipFirst).forEach(k => {
        newMembers[k] = rawMembers[k];
      });
      const newHasMore = count === PAGE_SIZE + 1;
      dispatch({
        type: RECEIVE_MORE_WORKFORCE_MEMBERS,
        value: newMembers,
        lastKey: newLastKey,
        hasMore: newHasMore,
      });
      if (newHasMore) {
        setTimeout(
          () => dispatch(loadMoreWorkforceMembers(app)),
          PREFETCH_DELAY_NEXT_MS,
        );
      }
    })
    .catch(err => {
      dispatch({
        type: REPORT_WORKFORCE_MEMBERS_REQUEST_ERROR,
        err: err && err.message ? err.message : 'Failed to load more',
      });
    });
};

export const getWorkforceGroupData = app => dispatch => {
  dispatch(requestTypes());

  firebase
    .database()
    .ref(`workforce_groups_data/${app}`)
    .on('value', snap => {
      if (snap.exists()) {
        dispatch({
          type: RECEIVE_WORKFORCE_GROUP_DATA,
          value: snap.val(),
        });
      } else {
        dispatch({
          type: RECEIVE_WORKFORCE_GROUP_DATA,
          value: {},
        });
      }
    });
};

export const filterTypeData = filter => dispatch =>
  dispatch({
    type: FILTER_WORKFORCE_MEMBER_DATA,
    payload: filter,
  });

export const setFilterQuery = filter => dispatch =>
  dispatch({
    type: SET_WORKFORCE_MEMBER_FILTER_QUERY,
    payload: filter,
  });

export const selectAllTypes = () => ({ type: SELECT_ALL_WORKFORCE_MEMBERS });
export const deselectAllTypes = () => ({
  type: DESELECT_ALL_WORKFORCE_MEMBERS,
});

export const reportActionError = (
  error,
  appname,
  selected,
  action,
) => dispatch =>
    dispatch({ type: WORKFORCE_DELETE_ERROR, error, appname, selected, action });

const deleteUser = (appname, memberID) =>
  new Promise((resolve, reject) => {
    const user = firebase.auth().currentUser;

    return user.getIdToken().then(token => {
      const headers = new Headers();
      headers.append('Content-Type', 'application/json');
      headers.append('x-access-token', token);

      return fetch(`${process.env.REACT_APP_WORKFORCE_API}/delete`, {
        method: 'delete',
        mode: 'cors',
        body: JSON.stringify({
          appname,
          memberID,
          requestOrigin: 'platform',
        }),
        headers,
      })
        .then(res => {
          if (res.status === 200) {
            return resolve(res);
          }

          throw new Error('Opps, something went wrong. Please try again.');
        })
        .catch(reject);
    });
  });

export const deleteMember = appname => (dispatch, getState) => {
  const { selected } = getState().auth.workforceMembers.listPage.data;
  selected.map(key =>
    deleteUser(appname, key)
      .then(data => {
        dispatch({
          type: WORKFORCE_MEMBER_DELETED,
          appname,
          data,
        });
        dispatch(openSnackbar(WORKFORCE_DELETED));
      })
      .catch(error => {
        dispatch(reportActionError(error));
      }),
  );
};
